import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../../services/users';
import { getFlowerToken } from '../../services/integrations';
import ThemeToggle from '../UI/ThemeToggle';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationPanel from '../Notifications/NotificationPanel';
import { useNotifications } from '../../hooks/useNotifications';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';
import NavigationHeader from './NavigationHeader';
import toast from 'react-hot-toast';

interface HeaderProps {
  showNavigation?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showNavigation = true }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { stats, refreshStats } = useNotifications();
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);

  useRealtimeNotifications({ onMessage: () => refreshStats() });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Componente para as ações do header (notificações, perfil, etc.)
  const HeaderActions = () => (
    <div className='flex items-center gap-x-4 lg:gap-x-6'>
        {/* Language Selector */}
        <LanguageSwitcher />
        {/* Theme Toggle */}
        <ThemeToggle />

      {/* Notifications */}
      <button
        type='button'
        onClick={() => setIsNotificationPanelOpen(true)}
        className='-m-2.5 p-2.5 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 relative'
      >
        <span className='sr-only'>Ver notificações</span>
        <BellIcon className='h-6 w-6' aria-hidden='true' />
        {stats && stats.unread > 0 && (
          <span className='absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center'>
            <span className='text-xs font-medium text-white'>{stats.unread > 99 ? '99+' : stats.unread}</span>
          </span>
        )}
      </button>

      {/* Separator */}
      <div className='hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200 dark:bg-gray-600' aria-hidden='true' />

      {/* Profile dropdown */}
      <Menu as='div' className='relative'>
        <Menu.Button className='-m-1.5 flex items-center p-1.5'>
          <span className='sr-only'>Abrir menu do usuário</span>
          <UserCircleIcon className='h-8 w-8 text-gray-400 dark:text-gray-300' />
          <span className='hidden lg:flex lg:items-center'>
            <span className='ml-4 text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100' aria-hidden='true'>
              {user?.full_name || user?.username}
            </span>
          </span>
        </Menu.Button>
        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items className='absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700'>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate('/profile')}
                  className={`block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900 dark:text-gray-100 ${
                    active ? 'bg-gray-50 dark:bg-gray-700' : ''
                  }`}
                >
                  Gerenciar conta
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate('/integrations')}
                  className={`block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900 dark:text-gray-100 ${
                    active ? 'bg-gray-50 dark:bg-gray-700' : ''
                  }`}
                >
                  Integrações
                </button>
              )}
            </Menu.Item>
            {user?.role === UserRole.ADMINISTRATOR && (
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={async () => {
                      try {
                        const token = await getFlowerToken();
                        window.open(`/api/v1/admin/flower/?_ftoken=${token}`, '_blank');
                      } catch {
                        toast.error('Não foi possível abrir o Monitor de Tarefas.');
                      }
                    }}
                    className={`block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900 dark:text-gray-100 ${
                      active ? 'bg-gray-50 dark:bg-gray-700' : ''
                    }`}
                  >
                    Monitor de Tarefas
                  </button>
                )}
              </Menu.Item>
            )}
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={logout}
                  className={`block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900 dark:text-gray-100 ${
                    active ? 'bg-gray-50 dark:bg-gray-700' : ''
                  }`}
                >
                  Sair
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );

  return (
    <>
      {showNavigation ? (
        <NavigationHeader
          onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
          headerActions={<HeaderActions />}
        />
      ) : (
        <div className='sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 dark:border-gray-700 dark:bg-gray-800'>
          <div className='flex flex-1 gap-x-4 self-stretch lg:gap-x-6'>
            <div className='relative flex flex-1'></div>
            <HeaderActions />
          </div>
        </div>
      )}

      {/* Notification Panel */}
      <NotificationPanel isOpen={isNotificationPanelOpen} onClose={() => setIsNotificationPanelOpen(false)} />
    </>
  );
};

export default Header;
